import { loadOperatorCurrency } from './store';

// The operator "currency" is the platform's own currency code — often NOT a
// real ISO-4217 code (e.g. "GC", "SC", "CO"). Intl currency-style formatting
// only accepts ISO-4217 and throws RangeError on anything else, so we probe
// once per code and fall back to a plain number + code label when it isn't ISO.
const isoSupport = new Map<string, boolean>();

function isIsoCurrency(currency: string): boolean {
  const cached = isoSupport.get(currency);
  if (cached !== undefined) return cached;
  let ok = false;
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency });
    ok = true;
  } catch {
    ok = false;
  }
  isoSupport.set(currency, ok);
  return ok;
}

/**
 * A plain number forces fixed-width output (min = max = N);
 * `{ max: N }` rounds to at most N decimals and drops trailing zeros
 * (`0.10` → `0.1`, `1.00` → `1`).
 */
export type FractionDigits = number | { max: number };

function fractionRange(
  amount: number,
  fractionDigits?: FractionDigits,
): { minimumFractionDigits: number; maximumFractionDigits: number } {
  if (typeof fractionDigits === 'object') {
    return { minimumFractionDigits: 0, maximumFractionDigits: fractionDigits.max };
  }
  const digits = fractionDigits ?? (Number.isInteger(amount) ? 0 : 2);
  return { minimumFractionDigits: digits, maximumFractionDigits: digits };
}

/**
 * Pure formatter: amount in the currency's main unit (no division by 100) +
 * a currency code. Real ISO-4217 codes render with their symbol (`$25.50`);
 * any other platform code renders as a labelled number (`25.50 CO`). Never
 * throws on an unknown code. Empty `currency` → empty string.
 */
export function formatAmount(amount: number, currency: string, fractionDigits?: FractionDigits): string {
  if (!currency) return '';
  const range = fractionRange(amount, fractionDigits);
  if (isIsoCurrency(currency)) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, ...range }).format(amount);
  }
  return `${new Intl.NumberFormat('en-US', range).format(amount)} ${currency}`;
}

/**
 * Formats an amount in the operator (platform) currency read from sessionStorage
 * via `loadOperatorCurrency()` — no hook, no reactivity.
 *
 * When `fractionDigits` is omitted, integers render without trailing zeros and
 * non-integers render with 2 decimals. Pass an explicit number to force
 * fixed-width output (`formatPrice(25, 2)` → `$25.00` / `25.00 GC`), or
 * `{ max: N }` to round to at most N decimals (`formatPrice(0.1, { max: 1 })`
 * → `$0.1`).
 *
 * Returns an empty string until the backend has shipped `operator_currency`.
 */
export function formatPrice(amount: number, fractionDigits?: FractionDigits): string {
  return formatAmount(amount, loadOperatorCurrency() ?? '', fractionDigits);
}

export function formatPriceRange(min: number, max: number): string {
  if (Math.abs(min - max) < 0.01) return formatPrice(min);
  return `${formatPrice(min)} - ${formatPrice(max)}`;
}

function isoSymbol(currency: string): string {
  const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency }).formatToParts(0);
  const sym = parts.find((p) => p.type === 'currency');
  return sym ? sym.value : currency;
}

export function currencySymbol(): string {
  const currency = loadOperatorCurrency();
  if (!currency) return '';
  return isIsoCurrency(currency) ? isoSymbol(currency) : currency;
}

/**
 * Where the currency marker sits relative to an (often animated) amount.
 * A real ISO-4217 currency is a leading symbol (`$`12.00); a platform code is
 * not a symbol, so it reads as a trailing label (12.00 `CO`). Lets callers
 * that render the number themselves (RollingNumber) place the marker right.
 */
export function affixFor(currency: string): { prefix: string; suffix: string } {
  if (!currency) return { prefix: '', suffix: '' };
  if (isIsoCurrency(currency)) return { prefix: isoSymbol(currency), suffix: '' };
  return { prefix: '', suffix: ` ${currency}` };
}

export function currencyAffix(): { prefix: string; suffix: string } {
  return affixFor(loadOperatorCurrency() ?? '');
}
